import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, ShieldCheck, Sparkle } from "@phosphor-icons/react";

const AUDIENCES = [
  { key: "candidate", label: "Job Seekers" },
  { key: "employer", label: "Employers" },
  { key: "recruiter", label: "Recruiters" },
];

const PLANS = {
  candidate: [
    { id: "candidate_free", name: "Free", price: { m: "€0", y: "€0" }, cadence: "", trial: null,
      tagline: "For finding realistic opportunities without paying to search.",
      cta: "Create free account", audienceOnly: false,
      features: ["1 active criteria profile", "Automatic Your Matches", "Manual job search and filters",
        "Eligibility-first matching", "Match explanations", "Saved jobs",
        "Open to Work / discoverability controls", "Privacy and consent controls"] },
    { id: "candidate_pro", name: "Pro", price: { m: "€9.90", y: "€89" }, cadence: { m: "/month", y: "/year" },
      trial: "14 days free", recommended: false,
      tagline: "For an active job search with more automation and application support.",
      cta: "Start 14-day free trial", plus: "Everything in Free, plus:",
      features: ["Multiple criteria profiles", "Advanced matching and filters", "Faster match alerts",
        "Targeted CV support", "Cover letter & application-answer support", "Application tracking",
        "Deeper match explanations", "Eligibility-change & job-change alerts"] },
  ],
  employer: [
    { id: "employer_starter", name: "Starter", price: { m: "€49", y: "€49" }, cadence: { m: "/month", y: "/month" },
      trial: "14 days free", tagline: "For occasional hiring or one active role.",
      cta: "Start 14-day free trial",
      features: ["1 active role", "Structured job requirements", "Mandatory / Preferred / Flexible criteria",
        "Eligibility-first candidate matching", "Explainable shortlist", "Candidate filtering funnel",
        "Invite to Apply", "Request Contact & consent workflow", "Basic Talent Reach"] },
    { id: "employer_growth", name: "Growth", price: { m: "€149", y: "€149" }, cadence: { m: "/month", y: "/month" },
      trial: "14 days free", recommended: true, tagline: "For teams hiring regularly.",
      cta: "Start 14-day free trial", plus: "Everything in Starter, plus:",
      features: ["Up to 5 active roles", "Advanced Talent Reach", "Requirement-impact drill-down",
        "Legal vs company-policy restriction visibility", "Pre-screen workflows", "Candidate pipeline",
        "Team access", "Analytics", "Reusable role templates"] },
    { id: "employer_enterprise", name: "Enterprise", price: { m: "From €399", y: "From €399" }, cadence: { m: "/month", y: "/month" },
      trial: "14-day pilot available", pilot: true, tagline: "For larger hiring teams and complex workflows.",
      cta: "Request 14-day pilot", plus: "Everything in Growth, plus:",
      features: ["Higher role & team limits", "Advanced permissions", "ATS integrations", "API access",
        "SSO options", "Custom reporting", "Enterprise controls", "Priority onboarding & support"] },
  ],
  recruiter: [
    { id: "recruiter_starter", name: "Starter", price: { m: "€99", y: "€99" }, cadence: { m: "/month", y: "/month" },
      trial: "14 days free", tagline: "For independent recruiters and smaller agencies.",
      cta: "Start 14-day free trial",
      features: ["1 recruiter seat", "Up to 10 active roles", "Structured role intake",
        "Automatic candidate matching", "Explainable shortlist", "Eligibility funnel",
        "Pre-screen workflows", "Invite and consent tools", "Basic Talent Reach"] },
    { id: "recruiter_pro", name: "Pro", price: { m: "€249", y: "€249" }, cadence: { m: "/month", y: "/month" },
      trial: "14 days free", recommended: true, tagline: "For teams managing multiple clients and searches.",
      cta: "Start 14-day free trial", plus: "Everything in Starter, plus:",
      features: ["Up to 3 recruiter seats", "Multiple clients and roles", "Candidate Pools",
        "Reusable matching workflows", "Advanced Talent Reach", "Client-facing reports",
        "Team collaboration", "Expanded analytics"] },
    { id: "recruiter_agency", name: "Agency / White-label", price: { m: "From €499", y: "From €499" }, cadence: { m: "/month", y: "/month" },
      trial: "14-day pilot available", pilot: true, tagline: "For agencies wanting the matching engine in their own brand.",
      cta: "Request 14-day pilot", plus: "Everything in Pro, plus:",
      features: ["White-label options", "Embeddable matching", "API & ATS integrations",
        "Higher client, role & candidate limits", "Advanced team permissions", "Organization administration",
        "Custom reporting", "Priority support"] },
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [aud, setAud] = useState("candidate");
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);

  const handleCta = async (plan) => {
    if (!user) { navigate("/register"); return; }
    if (plan.id === "candidate_free") { navigate("/dashboard"); return; }
    if (plan.audience && false) return;
    setBusy(plan.id);
    try {
      if (plan.pilot) {
        await api.post("/billing/request-pilot", { plan: plan.id });
        toast.success("Pilot request received — our team will reach out.");
      } else {
        const { data } = await api.post("/billing/start-trial", { plan: plan.id });
        toast.success(`Trial started — ${plan.name} active until ${new Date(data.subscription.trial_ends_at).toLocaleDateString()}`);
        navigate(user.role === "candidate" ? "/dashboard" : user.role === "employer" ? "/employer" : "/recruiter");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const plans = PLANS[aud];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading font-extrabold text-lg text-slate-900">Eligvia</Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/pricing" className="hidden sm:block"><Button variant="ghost" className="text-slate-900 font-medium">Pricing</Button></Link>
            {user ? (
              <Link to="/dashboard"><Button className="bg-slate-900 hover:bg-slate-800">Go to app</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="text-slate-700 px-3">Sign in</Button></Link>
                <Link to="/register"><Button className="bg-slate-900 hover:bg-slate-800 px-3">Create account</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tight">Simple pricing. Clear value.</h1>
          <p className="mt-4 text-base text-slate-600">Start with what you need today. Upgrade when you need more profiles, roles, automation or team tools.</p>
        </div>

        {/* Audience tabs */}
        <div className="mt-9 flex justify-center">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="pricing-audience-tabs">
            {AUDIENCES.map((a) => (
              <button key={a.key} onClick={() => setAud(a.key)} data-testid={`pricing-tab-${a.key}`}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-colors ${aud === a.key ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Monthly / Annual toggle */}
        <div className="mt-5 flex items-center justify-center gap-3 text-sm">
          <span className={annual ? "text-slate-400" : "text-slate-900 font-medium"}>Monthly</span>
          <button onClick={() => setAnnual(!annual)} data-testid="pricing-billing-toggle"
            className={`relative h-6 w-11 rounded-full transition-colors ${annual ? "bg-indigo-600" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${annual ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className={annual ? "text-slate-900 font-medium" : "text-slate-400"}>Annual</span>
        </div>

        {/* Plan cards */}
        <div className={`mt-10 grid gap-6 ${plans.length === 2 ? "sm:grid-cols-2 max-w-3xl mx-auto" : "lg:grid-cols-3"}`}>
          {plans.map((p) => {
            const price = annual ? p.price.y : p.price.m;
            const cadence = p.cadence ? (annual ? p.cadence.y : p.cadence.m) : "";
            const currentPlan = user && (user.role === aud) && false;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl border bg-white p-6 flex flex-col ${p.recommended ? "border-indigo-500 shadow-lg ring-1 ring-indigo-200" : "border-slate-200"}`}>
                {p.recommended && <span className="absolute -top-3 left-6 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Recommended</span>}
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-xl text-slate-900">{p.name}</h3>
                  {p.trial && <span className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">{p.trial}</span>}
                </div>
                <div className="mt-3">
                  <span className="font-heading font-extrabold text-3xl text-slate-900">{price}</span>
                  <span className="text-sm text-slate-500">{cadence}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600 min-h-[40px]">{p.tagline}</p>
                <Button onClick={() => handleCta(p)} disabled={busy === p.id} data-testid={`plan-cta-${p.id}`}
                  className={`mt-4 w-full ${p.recommended ? "bg-indigo-600 hover:bg-indigo-700" : "bg-slate-900 hover:bg-slate-800"}`}>
                  {busy === p.id ? "Please wait…" : p.cta}
                </Button>
                {p.plus && <p className="mt-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{p.plus}</p>}
                <ul className="mt-3 space-y-2">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check weight="bold" className="text-emerald-500 mt-0.5 shrink-0" size={15} />{f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Trust message */}
        <div className="mt-14 max-w-3xl mx-auto rounded-2xl border border-slate-200 bg-white p-7 text-center" data-testid="pricing-trust">
          <ShieldCheck weight="duotone" className="mx-auto text-indigo-500" size={30} />
          <h2 className="mt-2 font-heading font-bold text-xl text-slate-900">One matching standard on every plan</h2>
          <p className="mt-2 text-sm text-slate-600">We do not make eligibility less accurate on lower-priced plans. Paid plans unlock more capacity, automation, analytics and workflow tools — not more favorable match results.</p>
          <p className="mt-3 text-sm font-semibold text-slate-900 flex items-center justify-center gap-1.5"><Sparkle weight="fill" className="text-indigo-500" size={15} /> Eligibility first. Matching second.</p>
        </div>
      </div>
    </div>
  );
}
